/**
 * Hook de tutoriel : lancement automatique à la première visite, relance par le bouton
 * « ? ». Les deux passent par la file, jamais deux overlays en même temps.
 */
import { type Config, type Driver } from "driver.js";
import { useCallback, useEffect, useRef } from "react";
import { startTour, TourStep } from "./tourEngine";
import { arePageToursPaused, requestTour } from "./tourQueue";
import { hasSeenTour, markTourSeen } from "./tourStorage";

/** Le tour d'accueil passe avant le tutoriel de la page d'atterrissage. */
export const WELCOME_PRIORITY = 50;
const REPLAY_PRIORITY = 100;

export type UseTourOptions = {
  /** Clé du flag « déjà vu », scopé par compte. */
  id: string;
  /** Construites au lancement, pas au rendu : le filtrage des cibles lit le DOM. */
  steps: () => TourStep[];
  autoStart?: boolean;
  priority?: number;
  /** Délai avant inscription dans la file, le temps que la page finisse son rendu. */
  delayMs?: number;
  /** Soumis à la pause des tutoriels de page (faux pour le tour d'accueil, qui la pose). */
  pausable?: boolean;
  /** Réglages driver.js supplémentaires. */
  config?: Partial<Config>;
};

export const useTour = ({
  id,
  steps,
  autoStart = true,
  priority = 0,
  delayMs = 800,
  pausable = true,
  config,
}: UseTourOptions) => {
  const driverRef = useRef<Driver | null>(null);
  // En ref pour que `start` reste stable et ne relance pas l'auto-démarrage.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const configRef = useRef(config);
  configRef.current = config;

  const start = useCallback(() => {
    if (driverRef.current) return;

    const { onDestroyed, ...rest } = configRef.current ?? {};
    driverRef.current = startTour(stepsRef.current(), {
      ...rest,
      onDestroyed: (element, step, opts) => {
        driverRef.current = null;
        markTourSeen(id);
        onDestroyed?.(element, step, opts);
      },
    });
  }, [id]);

  useEffect(() => {
    if (!autoStart || hasSeenTour(id)) return;

    let cancel: (() => void) | undefined;
    const timer = setTimeout(() => {
      // Vérifié au dernier moment : le tour d'accueil peut poser la pause après que ce
      // minuteur a été armé.
      if (pausable && arePageToursPaused()) return;
      cancel = requestTour(start, priority);
    }, delayMs);

    return () => {
      clearTimeout(timer);
      cancel?.();
    };
  }, [autoStart, delayMs, id, pausable, priority, start]);

  // Navigation pendant un tour : on ferme, ce qui libère aussi le verrou.
  useEffect(() => () => driverRef.current?.destroy(), []);

  const replay = useCallback(() => {
    requestTour(start, REPLAY_PRIORITY);
  }, [start]);

  return { start: replay };
};
